import { useEffect, useRef } from "react";
import { motion, useReducedMotion, useInView, animate } from "framer-motion";
import * as Icons from "lucide-react";
import { fmt, fmtDate } from "@/components/shared";
import { CountUp } from "@/components/PlanVisuals";

const ease = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, className = "", ...rest }) {
  const reduce = useReducedMotion();
  return (
    <motion.div initial={reduce ? false : { opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease, delay }} className={className} {...rest}>{children}</motion.div>
  );
}

export function AvailabilityRing({ available = 0, total = 0, size = 180, testId = "share-availability-ring" }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const pct = total ? Math.min(100, (available / total) * 100) : 0;
  const r = (size - 16) / 2, c = 2 * Math.PI * r;
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const ctrl = animate(reduce ? pct : 0, pct, { duration: reduce ? 0 : 1.4, ease, onUpdate: (v) => { node.style.strokeDashoffset = `${c - (c * v) / 100}`; } });
    return () => ctrl.stop();
  }, [pct, c, reduce]);
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }} data-testid={testId}>
      <span className="absolute inset-4 rounded-full bg-cyan-400/10 blur-2xl share-ring-glow" aria-hidden="true" />
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(148,163,184,.15)" strokeWidth="10" fill="none" />
        <circle ref={ref} cx={size / 2} cy={size / 2} r={r} stroke="url(#shareRingGrad)" strokeWidth="10" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c} />
        <defs><linearGradient id="shareRingGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#00F0FF" /><stop offset="100%" stopColor="#2563EB" /></linearGradient></defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="font-mono text-2xl font-bold text-white"><CountUp value={Math.round(pct)} />%</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">available</span>
      </div>
    </div>
  );
}

export function ShareStat({ label, value, suffix, decimals = 0, testId, accent = "text-white" }) {
  return (
    <div className="glass glass-hover rounded-2xl p-5" data-testid={testId}>
      <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`font-mono text-2xl font-bold mt-2 ${accent}`}><CountUp value={Number(value) || 0} decimals={decimals} /> {suffix && <span className="text-sm text-slate-500 font-sans">{suffix}</span>}</p>
    </div>
  );
}

export const PROJECT_STATUS = {
  coming_soon: ["Coming Soon", "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"],
  in_development: ["In Development", "bg-blue-500/15 text-blue-300 border-blue-500/30"],
  testing: ["Testing", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
  launching_soon: ["Launching Soon", "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30"],
  live: ["Live", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"],
  completed: ["Completed", "bg-slate-500/15 text-slate-300 border-slate-500/30"],
  paused: ["Paused", "bg-red-500/15 text-red-300 border-red-500/30"],
};

export function ProjectStatusChip({ status }) {
  const [label, cls] = PROJECT_STATUS[status] || PROJECT_STATUS.coming_soon;
  return <span data-testid={`project-status-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}><span className="w-1.5 h-1.5 rounded-full bg-current share-dot-pulse" />{label}</span>;
}

export function ProjectIcon({ name, className = "w-6 h-6" }) {
  const Icon = Icons[name] || Icons.Sparkles;
  return <Icon className={className} />;
}

export function ProjectCard({ project, index = 0, onLearnMore }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} data-testid={`project-card-${project.id}`}
      initial={reduce ? false : { opacity: 0, y: 26, scale: 0.97 }} animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.65, ease, delay: Math.min((index % 6) * 0.1, 0.5) }} whileHover={reduce ? {} : { y: -6 }}
      className="plan-card group relative rounded-2xl p-6 bg-[#0B1224]/80 backdrop-blur-xl border border-cyan-500/15 hover:border-cyan-400/50 hover:shadow-[0_20px_60px_-15px_rgba(0,200,255,0.3)] transition-[border-color,box-shadow] duration-500 flex flex-col">
      <span className="plan-card-border" aria-hidden="true" />
      <div className="flex items-start justify-between gap-3">
        <div className="w-12 h-12 rounded-xl bg-cyan-500/15 border border-cyan-500/20 flex items-center justify-center text-cyan-300 overflow-hidden share-icon-glow">
          {project.image_url ? <img src={project.image_url} alt="" className="w-full h-full object-cover" /> : <ProjectIcon name={project.icon} />}
        </div>
        <ProjectStatusChip status={project.status} />
      </div>
      <p className="label-eyebrow text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400 mt-5">{project.category}</p>
      <h3 className="font-heading text-lg font-semibold text-white mt-1">{project.name}</h3>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{project.description}</p>
      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="text-xs text-slate-500 font-mono">{project.launch_date ? `Launch · ${fmtDate(project.launch_date)}` : "Launch date TBA"}</span>
        <button type="button" onClick={() => onLearnMore?.(project)} data-testid={`project-learn-more-${project.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-200 transition-colors group/btn">
          Learn More <Icons.ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
        </button>
      </div>
    </motion.div>
  );
}

export function priceLabel(p) { return `${fmt(p)} USDT`; }
